import {
  Skeleton as ChakraSkeleton,
  SkeletonProps as ChakraSkeletonProps,
} from '@chakra-ui/skeleton'
import { keyframes } from '@chakra-ui/system'
import * as React from 'react'

import { useBgColorCtx } from '~theme/BgColorProvider'
import { getRadiusValue, TRadius } from '~ui/styleGuide/radiuses'
import { getSpacingValue, TMargin } from '~ui/styleGuide/spaces'
import { getDurationValueMs, TDuration } from '~ui/styleGuide/styles'

// https://github.com/chakra-ui/chakra-ui/blob/main/packages/theme/src/components/skeleton.ts

// ? should be in theme?
const defaultDarkStartColor = `rgba(0, 0, 0, 0.1)`
const defaultLightStartColor = `rgba(255, 255, 255, 0.1)`
const defaultDarkEndColor = `rgba(0, 0, 0, 0.2)`
const defaultLightEndColor = `rgba(255, 255, 255, 0.2)`

type TSkeletonOptions = {
  /**
   * one of the colors use in the Skeleton animation
   */
  bgColor?: string
  /**
   * one of the colors use in the Skeleton animation
   */
  fgColor?: string
  /**
   * border radius of the Skeleton shape
   */
  radius?: TRadius
  /**
   * margin of the Skeleton
   * can be single space key,
   * object with `horizontal` and `vertical` attributes
   * or separate values for each side `top`, `right`, `bottom`, `left
   */
  margin?: TMargin
  /**
   * how long/fast will the animation be
   */
  animationDuration?: TDuration
}

const fade = (startColor: string, endColor: string) =>
  keyframes({
    from: { borderColor: startColor, background: startColor },
    to: { borderColor: endColor, background: endColor },
  })

type TGetSkeletonStylesOptions = TSkeletonOptions & {
  fgColor: Defined<TSkeletonOptions['fgColor']>
  bgColor: Defined<TSkeletonOptions['bgColor']>
}
const getSkeletonStyles = ({
  radius = `small`,
  margin = `none`,
  fgColor,
  bgColor,
  animationDuration = `medium`,
}: TGetSkeletonStylesOptions): Partial<ChakraSkeletonProps> => {
  const radiusValue = getRadiusValue(radius)
  const marginValue = getSpacingValue(margin)
  const durationValue = getDurationValueMs(animationDuration)

  return {
    opacity: 0.7,
    cursor: `progress`,
    background: fgColor,
    borderColor: bgColor,
    title: `Loading...`,
    pointerEvents: `auto`, // needed for the cursor & title
    borderRadius: radiusValue,
    margin: marginValue,
    animation: `${durationValue}ms linear infinite alternate ${fade(bgColor, fgColor)}`,
  }
}

type TChakraProps = Pick<ChakraSkeletonProps, 'height' | 'maxWidth' | 'display' | 'alignSelf'>

type TProps = Children & TChakraProps & TSkeletonOptions & SkeletonProps

/**
 * loading placeholder skeleton
 * use to present some temporal UI while loading data
 */
export const Skeleton: React.FC<TProps> = ({
  radius,
  margin,
  bgColor,
  fgColor,
  children,
  animationDuration,
  showSkeleton = true,
  display,
  ...chakraProps
}) => {
  const { fgColorShade } = useBgColorCtx()

  // prettier-ignore
  const themedBgColorValue = fgColorShade === `light` ? defaultLightStartColor : defaultDarkStartColor
  const themedFgColorValue = fgColorShade === `light` ? defaultLightEndColor : defaultDarkEndColor
  const bgColorValue = bgColor ?? themedBgColorValue
  const fgColorValue = fgColor ?? themedFgColorValue

  const skeletonStyles = showSkeleton
    ? getSkeletonStyles({
        margin,
        radius,
        animationDuration,
        bgColor: bgColorValue,
        fgColor: fgColorValue,
      })
    : {}

  const skeletonChakraProps = showSkeleton ? chakraProps : {}

  return (
    <ChakraSkeleton
      {...skeletonChakraProps}
      {...skeletonStyles}
      display={display}
      isLoaded={!showSkeleton}
    >
      {children}
    </ChakraSkeleton>
  )
}
